import type { BlockKey, WindBlock } from '../tiles/format';
import { gridLngLat } from '../tiles/grid';
import { BLOCK } from '../tiles/levels';
import type { ModelPlan } from './viewPlan';
import { sampleLayer, type WindLayer } from './windField';

/** Distance over which a regional model fades in from its domain's edge, km. */
const FADE_KM = 40;

/** The blocks a plan needs: for each block, one per valid time around the shown time. */
export function planBlocks(plan: ModelPlan): BlockKey[] {
  const { model, run, level, times, blocks } = plan;
  const valids = times.a === times.b ? [times.a] : [times.a, times.b];
  return blocks.flatMap(({ bx, by }) =>
    valids.map((valid) => ({ model: model.id, run, valid, level, bx, by })),
  );
}

/**
 * A plan's layer from the blocks loaded so far (`blockOf`), interpolated between the two valid times.
 * Points of blocks not loaded yet keep the values `previous` showed there (the model's layer before the
 * view or time changed), so the map never blanks while loading; elsewhere they're NaN.
 */
export function assembleLayer(
  plan: ModelPlan,
  blockOf: (key: BlockKey) => WindBlock | undefined,
  previous: WindLayer | undefined,
): WindLayer {
  const { model, run, level, grid, times, blocks } = plan;
  const columnOf = (b: { bx: number; turn: number }) => b.turn * grid.nx + b.bx * BLOCK;
  const i0 = Math.min(...blocks.map(columnOf));
  const j0 = Math.min(...blocks.map((b) => b.by * BLOCK));
  const nx = Math.max(...blocks.map(columnOf)) + BLOCK + 1 - i0;
  const ny = Math.max(...blocks.map((b) => b.by * BLOCK)) + BLOCK + 1 - j0;
  const u = new Float32Array(nx * ny).fill(NaN);
  const v = new Float32Array(nx * ny).fill(NaN);
  const isGlobal = grid.kind === 'latlon' && grid.wraps;
  const layer: WindLayer = {
    model: model.id,
    grid,
    i0,
    j0,
    nx,
    ny,
    u,
    v,
    fade: isGlobal ? 0 : FADE_KM / (model.km * 2 ** level),
  };

  const wind: [number, number] = [0, 0];
  for (const b of blocks) {
    const key = { model: model.id, run, level, bx: b.bx, by: b.by };
    const a = blockOf({ ...key, valid: times.a });
    const z = times.a === times.b ? a : blockOf({ ...key, valid: times.b });
    const x0 = columnOf(b) - i0;
    const y0 = b.by * BLOCK - j0;
    for (let y = 0; y <= BLOCK; y++) {
      for (let x = 0; x <= BLOCK; x++) {
        const out = (y0 + y) * nx + x0 + x;
        const k = y * (BLOCK + 1) + x;
        if (a && z) {
          u[out] = a.u[k] + (z.u[k] - a.u[k]) * times.t;
          v[out] = a.v[k] + (z.v[k] - a.v[k]) * times.t;
        } else if (a || z) {
          // One time loaded: show it until the other arrives.
          const only = (a ?? z)!;
          u[out] = only.u[k];
          v[out] = only.v[k];
        } else if (previous && Number.isNaN(u[out])) {
          // (Unless a loaded neighbour filled it already: blocks share their edges.)
          const [lng, lat] = gridLngLat(grid, i0 + x0 + x, j0 + y0 + y);
          if (sampleLayer(previous, lng, lat, wind) > 0) [u[out], v[out]] = wind;
        }
      }
    }
  }
  return layer;
}
